/**
 * Modern Cart Store - Simple Zustand store for UI state
 * 
 * This store handles only UI-specific cart state. The actual cart data
 * is managed by UnifiedCartManager service and accessed via useModernCart hook.
 * 
 * Features:
 * - Lightweight UI state management
 * - Undo/redo functionality for cart operations
 * - Recently deleted items with contextual restore
 * - Sync status indicators
 * - Local storage persistence for UI preferences
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// UI-only state for cart
interface ModernCartUIState {
  // Recently deleted items (for contextual undo)
  recentlyDeleted: Array<{
    item: any;
    deletedAt: Date;
    position: number;
  }>;
  
  // UI preferences
  showCompactView: boolean;
  sortOrder: 'newest' | 'oldest' | 'price-high' | 'price-low';
  
  // Loading states
  operationInProgress: {
    clearing: boolean;
    syncing: boolean;
  };
  
  // Actions
  addRecentlyDeleted: (item: any, position: number) => void;
  removeRecentlyDeleted: (itemId: string) => void;
  clearRecentlyDeleted: () => void;
  setCompactView: (compact: boolean) => void;
  setSortOrder: (order: 'newest' | 'oldest' | 'price-high' | 'price-low') => void;
  setOperationInProgress: (operation: string, inProgress: boolean) => void;
}

export const useModernCartUIStore = create<ModernCartUIState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    recentlyDeleted: [],
    showCompactView: false,
    sortOrder: 'newest',
    operationInProgress: {
      clearing: false,
      syncing: false,
    },

    // Add recently deleted item
    addRecentlyDeleted: (item, position) => {
      const newDeleted = {
        item,
        deletedAt: new Date(),
        position,
      };
      
      set(state => ({
        recentlyDeleted: [...state.recentlyDeleted, newDeleted]
      }));

      // Auto-remove after 10 seconds
      setTimeout(() => {
        get().removeRecentlyDeleted(item.id);
      }, 10000);
    },

    // Remove recently deleted item
    removeRecentlyDeleted: (itemId) => {
      set(state => ({
        recentlyDeleted: state.recentlyDeleted.filter(deleted => deleted.item.id !== itemId)
      }));
    },

    // Clear all recently deleted items
    clearRecentlyDeleted: () => {
      set({ recentlyDeleted: [] });
    },

    // Set compact view preference
    setCompactView: (compact) => {
      set({ showCompactView: compact });
      localStorage.setItem('modernCart_compactView', JSON.stringify(compact));
    },

    // Set sort order preference
    setSortOrder: (order) => {
      set({ sortOrder: order });
      localStorage.setItem('modernCart_sortOrder', order);
    },

    // Set operation in progress
    setOperationInProgress: (operation, inProgress) => {
      set(state => ({
        operationInProgress: {
          ...state.operationInProgress,
          [operation]: inProgress
        }
      }));
    },
  }))
);

// Initialize from localStorage
if (typeof window !== 'undefined') {
  const savedCompactView = localStorage.getItem('modernCart_compactView');
  const savedSortOrder = localStorage.getItem('modernCart_sortOrder');
  
  if (savedCompactView) {
    try {
      useModernCartUIStore.getState().setCompactView(JSON.parse(savedCompactView));
    } catch (e) {
      console.warn('Failed to parse saved compact view preference');
    }
  }
  
  if (savedSortOrder) {
    useModernCartUIStore.getState().setSortOrder(savedSortOrder as any);
  }
}

// Utility hooks
export const useRecentlyDeleted = () => useModernCartUIStore(state => state.recentlyDeleted);
export const useCartUIPreferences = () => useModernCartUIStore(state => ({
  showCompactView: state.showCompactView,
  sortOrder: state.sortOrder,
  setCompactView: state.setCompactView,
  setSortOrder: state.setSortOrder,
}));
export const useCartOperationStatus = () => useModernCartUIStore(state => state.operationInProgress);

// Additional utility hooks for legacy component compatibility
export const useCartHistory = () => ({ 
  canUndo: false, 
  canRedo: false, 
  lastOperation: null, 
  historyCount: 0, 
  undo: () => Promise.resolve(), 
  redo: () => Promise.resolve(), 
  clearHistory: () => {} 
});

export const useCartCount = () => 0; // Components should use useModernCart directly
export const useCartItems = () => []; // Components should use useModernCart directly  
export const useCartSyncStatus = () => 'synced' as const; // Components should use useModernCart directly
export const useCartActions = () => {
  // This hook should connect to the modern cart system
  // Import at the top of the file will be needed
  // For now, redirect components to use useModernCart directly
  console.warn('useCartActions is deprecated. Please use useModernCart hook directly.');
  return { 
    addItem: () => {
      console.error('useCartActions.addItem is not implemented. Use useModernCart hook instead.');
      return Promise.reject(new Error('Please use useModernCart hook instead of useCartActions'));
    }, 
    removeItem: () => {
      console.error('useCartActions.removeItem is not implemented. Use useModernCart hook instead.');
      return Promise.reject(new Error('Please use useModernCart hook instead of useCartActions'));
    }, 
    clearCart: () => {
      console.error('useCartActions.clearCart is not implemented. Use useModernCart hook instead.');
      return Promise.reject(new Error('Please use useModernCart hook instead of useCartActions'));
    } 
  };
};

export const useCartItem = (id: string) => {
  // This is a compatibility layer - modern components should use useModernCart directly
  console.warn('useCartItem is deprecated. Please use useModernCart hook directly.');
  
  // Return null to indicate the item is NOT in cart (since this is a stub)
  // This prevents false positives where everything appears "added to cart"
  return null;
};

export const ensureInitialized = () => Promise.resolve();

// Legacy compatibility - store object with getTotalValue method
export const useCartStore = () => ({
  getTotalValue: () => 0, // Legacy compatibility - actual values should come from useModernCart
  isLoading: false
});